/**
 * Immutable edits to a data document — the writer ADR-127 D2's form needs.
 *
 * WHY THIS IS ITS OWN MODULE, AND WHY IT WAS NOT SHIPPED WITH THE FORM
 * -------------------------------------------------------------------
 * The form landed with its controls read-only on purpose: "a partial writer would let an
 * operator edit a row and lose it." That is the failure mode this module exists to make
 * impossible, and it is worth naming precisely, because it has three separate causes and a
 * plausible implementation hits all three:
 *
 *   1. MUTATION. Editing the object React holds in state means the next render may not see a
 *      change (same reference) while the document has already changed. The operator's edit
 *      appears to vanish, and their next keystroke is applied to a document nobody rendered.
 *   2. CLOBBERING. Rebuilding a row from the fields the form knows about drops anything it
 *      does not — provenance, an unmodelled `oneOf`, a field added by a newer schema version.
 *      The data loss is invisible until someone reads the diff.
 *   3. INDEX DRIFT. Removing or moving a row by anything other than its exact index deletes
 *      the wrong one. With `x-key` records this is silent: the document still validates.
 *
 * So every function here is a pure, structurally-shared copy, addressed by an explicit
 * SEGMENT PATH rather than a dotted string. Segments avoid the ambiguity that makes string
 * paths quietly wrong: a property may legitimately contain a dot, and `items[2]` needs
 * parsing that `['items', 2]` does not.
 *
 * THE BOUND IS ENFORCED HERE, NOT IN THE BUTTON
 * ---------------------------------------------
 * `maxItems` is the number a `for.in` inherits (ADR-126 D4) and the runtime blast-radius cap
 * (ADR-131 D7). `addRow` REFUSES past it and says why, rather than the UI merely disabling a
 * button — a disabled button with no reason is what sends an operator to raw mode, where the
 * bound is not enforced at all.
 */

/** A path segment: a property name, or an array index. */
export type Segment = string | number

export interface EditResult<T = unknown> {
  doc: T
  /** Set when the edit was REFUSED. The caller shows it; nothing silently no-ops. */
  refused?: string
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Read a segment path. `undefined` for anything absent — never a thrown error, because a
 *  form renders paths that do not exist yet. */
export function getAt(doc: unknown, path: Segment[]): unknown {
  let cur: unknown = doc
  for (const segment of path) {
    if (typeof segment === 'number') {
      if (!Array.isArray(cur)) return undefined
      cur = cur[segment]
    } else {
      if (!isPlainObject(cur)) return undefined
      cur = cur[segment]
    }
  }
  return cur
}

/**
 * Set a value at a segment path, copying only the containers along it.
 *
 * Everything off the path keeps its identity — which is both the performance property React
 * wants and the correctness one: a sibling field cannot be dropped by a write it was not part
 * of, because it is never rebuilt.
 *
 * `undefined` DELETES the key rather than storing `undefined`. JSON has no undefined, so
 * storing it would serialise to `null` and read as a deliberate null — a different fact from
 * "not set", and one an operator never asked for.
 */
export function setAt<T>(doc: T, path: Segment[], value: unknown): T {
  if (path.length === 0) return value as T
  const [head, ...rest] = path

  if (typeof head === 'number') {
    const base = Array.isArray(doc) ? doc : []
    const next = base.slice()
    // Writing past the end would silently create holes (undefined entries that serialise to
    // null). Refuse by returning the document unchanged; addRow is how a row is created.
    if (head < 0 || head >= next.length) return doc
    const child = rest.length === 0 ? value : setAt(next[head], rest, value)
    // Identity means "nothing changed", so a refused nested write must not manufacture a new
    // reference: React would re-render for an edit that did not happen, and a caller could
    // not use identity to detect a no-op.
    if (rest.length > 0 && child === next[head]) return doc
    next[head] = child
    return next as unknown as T
  }

  const base = isPlainObject(doc) ? doc : {}
  const next: Record<string, unknown> = { ...base }
  if (rest.length === 0) {
    if (value === undefined) {
      delete next[head]
    } else {
      next[head] = value
    }
  } else {
    const child = setAt(next[head], rest, value)
    if (child === next[head]) return doc   // see the note above
    next[head] = child
  }
  return next as unknown as T
}

/**
 * Append a row to the array at `path`, honouring the declared bound.
 *
 * `template` is the new row. The caller builds it from the schema's required fields, so an
 * added row is valid-shaped from the first render instead of failing validation before the
 * operator has typed anything.
 */
export function addRow<T>(
  doc: T, path: Segment[], template: unknown, maxItems?: number,
): EditResult<T> {
  const current = getAt(doc, path)
  const rows = Array.isArray(current) ? current : []
  if (typeof maxItems === 'number' && rows.length >= maxItems) {
    return {
      doc,
      refused: `This set declares a maximum of ${maxItems} records, and it already has ` +
        `${rows.length}. The bound is what a workflow's loop inherits, so raising it is a ` +
        `schema change rather than a data one.`,
    }
  }
  return { doc: setAt(doc, path, [...rows, template]) }
}

/** Remove exactly one row, by index. */
export function removeRow<T>(doc: T, path: Segment[], index: number): EditResult<T> {
  const current = getAt(doc, path)
  if (!Array.isArray(current)) return { doc, refused: 'There is no list here to remove from.' }
  if (index < 0 || index >= current.length) {
    // Refuse rather than clamp. A stale index means the UI and the document disagree about
    // what is on screen, and deleting the nearest row instead is how the wrong record goes.
    return { doc, refused: `Row ${index + 1} is no longer there — reload before deleting.` }
  }
  const next = current.slice()
  next.splice(index, 1)
  return { doc: setAt(doc, path, next) }
}

/**
 * Move a row from one index to another (reorder).
 *
 * Remove-then-insert, in that order, because the destination index is expressed against the
 * list the operator can see. Inserting first would shift the source and move the wrong row —
 * an off-by-one that looks like it worked.
 */
export function moveRow<T>(doc: T, path: Segment[], from: number, to: number): EditResult<T> {
  const current = getAt(doc, path)
  if (!Array.isArray(current)) return { doc, refused: 'There is no list here to reorder.' }
  if (from < 0 || from >= current.length) {
    return { doc, refused: `Row ${from + 1} is no longer there — reload before reordering.` }
  }
  const target = Math.max(0, Math.min(to, current.length - 1))
  if (target === from) return { doc }
  const next = current.slice()
  const [row] = next.splice(from, 1)
  next.splice(target, 0, row)
  return { doc: setAt(doc, path, next) }
}

/**
 * A new row shaped by the schema's required fields.
 *
 * Required fields get a typed empty value so the row renders and validates predictably;
 * OPTIONAL fields are left ABSENT rather than filled with empties. An absent optional field
 * and one set to "" are different facts, and pre-filling would turn every added row into an
 * assertion the operator never made.
 */
export function templateRow(recordSchema: unknown): Record<string, unknown> {
  if (!isPlainObject(recordSchema)) return {}
  const props = isPlainObject(recordSchema.properties) ? recordSchema.properties : {}
  const required = Array.isArray(recordSchema.required)
    ? (recordSchema.required as string[])
    : []
  const row: Record<string, unknown> = {}
  for (const name of required) {
    const node = props[name]
    const type = isPlainObject(node) ? node.type : undefined
    const primary = Array.isArray(type) ? type.find((t) => t !== 'null') : type
    if (primary === 'number' || primary === 'integer') row[name] = 0
    else if (primary === 'boolean') row[name] = false
    else if (primary === 'array') row[name] = []
    else if (primary === 'object') row[name] = {}
    else row[name] = ''
  }
  return row
}

/** The segment path for one field of one row: `properties[].key` + index 2 → ['properties', 2, 'key']. */
export function rowFieldPath(descriptorPath: string, index: number): Segment[] {
  const out: Segment[] = []
  for (const raw of descriptorPath.split('.')) {
    if (raw.endsWith('[]')) {
      out.push(raw.slice(0, -2), index)
    } else if (raw) {
      out.push(raw)
    }
  }
  return out
}

/** The segment path of the collection itself: `properties[].key` → ['properties']. */
export function collectionPath(descriptorPath: string): Segment[] {
  const out: Segment[] = []
  for (const raw of descriptorPath.split('.')) {
    if (raw.endsWith('[]')) {
      out.push(raw.slice(0, -2))
      return out
    }
    if (raw) out.push(raw)
  }
  return out
}


/**
 * A data document as it is COMMITTED: two-space indent, trailing newline.
 *
 * Lives here rather than in the page because it is a property of the document, not of the
 * surface — and because a save that reformats the file produces a whole-file diff, which
 * buries the one line that actually changed in a review.
 */
export function serializeDocument(doc: unknown): string {
  return `${JSON.stringify(doc, null, 2)}\n`
}

